import React from 'react';
import {
  CaptionText,
  FlexColumn,
  formatNumber,
  DEFAULT_AMOUNT_PRECISION,
} from '@make-software/cspr-ui';
import { BodyText } from '@block-explorer/ui';

import { Cspr } from '../cspr/cspr';
import {
  currencyPrecisionByCase,
  motesToCSPR,
  PrecisionCase,
} from '../../utils/currency';
import Big from 'big.js';
import Skeleton from 'react-loading-skeleton';
import FiatCurrency from '../multi-currency/multi-currency';
import Cep18FormattedAmount from '../cep18/cep18';
import { FTAmountDataType } from '@block-explorer/entities';
import { tokenDivider } from '../../hooks/use-get-cep18-amount-from-metadata';

export interface TableDataCurrencyAmountProps {
  csprCurrencyRate: number | undefined;
  amount: string | undefined | null;
  outgoingAmount?: boolean;
  loading: boolean;
  cep18AmountData?: Omit<FTAmountDataType, 'totalSupply'> | null;
  isCSPR?: boolean;
  precisionCase?: PrecisionCase;
}

const FormatAmount = ({
  amount,
  currencyAmount,
  isAmountInCSPR,
  precisionCase,
}) => {
  if (isAmountInCSPR) {
    return (
      <>
        <Cspr
          motes={amount}
          precisionCase={precisionCase || PrecisionCase.deployCost}
        />
        <CaptionText size={2} variation="darkGray">
          <FiatCurrency
            amount={currencyAmount}
            precision={
              precisionCase
                ? currencyPrecisionByCase(precisionCase)
                : DEFAULT_AMOUNT_PRECISION
            }
          />
        </CaptionText>
      </>
    );
  }

  if (amount == 0) {
    return '-';
  }

  return (
    <>
      {formatNumber(amount, { precision: 0 })}
      <CaptionText size={2} variation="darkGray">
        {'-'}
      </CaptionText>
    </>
  );
};

export const TableDataCurrencyAmount = ({
  amount,
  cep18AmountData,
  csprCurrencyRate,
  loading,
  outgoingAmount,
  isCSPR = true,
  precisionCase,
}: TableDataCurrencyAmountProps) => {
  const csprAmount = amount ? motesToCSPR(amount) : 0;
  const currencyAmount =
    csprCurrencyRate && csprAmount
      ? Big(csprAmount).mul(csprCurrencyRate).toString()
      : null;

  if (loading) {
    return (
      <BodyText size={3}>
        <Skeleton />
      </BodyText>
    );
  }

  if (cep18AmountData?.symbol) {
    const calculatedCep18Amount =
      cep18AmountData.decimals && amount
        ? Big(amount).div(tokenDivider(cep18AmountData.decimals)).toString()
        : null;

    return (
      <BodyText size={3} monotype noWrap>
        <Cep18FormattedAmount
          amount={calculatedCep18Amount}
          symbol={cep18AmountData.symbol}
          precisionCase={precisionCase || PrecisionCase.deployCost}
        />
      </BodyText>
    );
  }

  return (
    <BodyText size={3} monotype noWrap>
      {amount == null ? (
        '-'
      ) : (
        <FlexColumn>
          {outgoingAmount && '-'}
          <FormatAmount
            amount={amount}
            currencyAmount={currencyAmount}
            isAmountInCSPR={isCSPR}
            precisionCase={precisionCase}
          />
        </FlexColumn>
      )}
    </BodyText>
  );
};
